/**
 * Cart drawer: open/close, quantity changes via /cart/change.js,
 * re-render via the Section Rendering API.
 *
 * Other components can open it with:
 *   document.querySelector('cart-drawer')?.open()
 * or refresh it after an add-to-cart with:
 *   document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }))
 */
window.CartDrawer = class CartDrawer extends HTMLElement {
  #abort = null;
  #controller = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.addEventListener('change', (event) => this.#handleChange(event), { signal });

    document.addEventListener('cart:refresh', (event) => {
      this.refresh(event.detail?.open ?? false);
    }, { signal });

    this.#loadUpsells();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#controller?.abort();
  }

  open() {
    const dialog = this.querySelector('dialog');
    if (dialog && !dialog.open) dialog.showModal();
  }

  close() {
    const dialog = this.querySelector('dialog');
    if (!dialog?.open) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close();
      return;
    }

    dialog.setAttribute('data-closing', '');
    dialog.addEventListener('animationend', () => {
      dialog.removeAttribute('data-closing');
      dialog.close();
    }, { once: true });
  }

  get #isOpen() {
    return this.querySelector('dialog')?.open ?? false;
  }

  #handleClick(event) {
    // Backdrop click: the dialog itself is the target only when clicking
    // outside its content. Delegated here so it survives innerHTML refreshes.
    if (event.target.matches('dialog')) {
      this.close();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      this.close();
      return;
    }

    if (event.target.closest('[data-coupon-apply]')) {
      WsCoupon.apply(this);
      return;
    }

    const couponRemove = event.target.closest('[data-coupon-remove]');
    if (couponRemove) {
      WsCoupon.remove(this, couponRemove.dataset.couponRemove);
      return;
    }

    const stepper = event.target.closest('[data-cart-qty-change]');
    if (stepper) {
      const input = this.querySelector(`[data-cart-qty][data-line="${stepper.dataset.line}"]`);
      const quantity = Math.max(0, Number(input?.value ?? 0) + Number(stepper.dataset.cartQtyChange));
      this.#changeLine(stepper.dataset.line, quantity);
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) this.#changeLine(remove.dataset.line, 0);
  }

  #handleChange(event) {
    const input = event.target.closest('[data-cart-qty]');
    if (!input) return;
    this.#changeLine(input.dataset.line, Math.max(0, Number(input.value)));
  }

  async #changeLine(line, quantity) {
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cart = await response.json();
      this.#updateCounts(cart.item_count);
      await this.refresh(this.#isOpen);

      this.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
    } catch (error) {
      console.error('Cart change failed:', error);
      await this.refresh(this.#isOpen);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  async refresh(openAfter = false) {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const url = new URL(window.location.href);
    url.searchParams.set('sections', this.dataset.sectionId);

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const sections = await response.json();
      const html = sections[this.dataset.sectionId];
      if (!html) return;

      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const next = fragment.querySelector('cart-drawer');
      if (!next) return;

      // Keep the existing <dialog> element alive so an open drawer stays open
      // (no close/re-open flicker or replayed slide-in animation) — only its
      // content is swapped.
      const currentDialog = this.querySelector('dialog');
      const nextDialog = next.querySelector('dialog');

      // Preserve focus across the content swap (WCAG 2.4.3): remember which
      // control was focused, then restore it (or fall back to the close button)
      // after re-render so focus never drops to <body> behind the open drawer.
      const active = document.activeElement;
      let focusKey = null;
      if (currentDialog && currentDialog.contains(active)) {
        const attr = ['data-cart-qty-change', 'data-cart-qty', 'data-cart-remove', 'data-cart-close']
          .find((k) => active.hasAttribute(k));
        if (attr) focusKey = { attr, val: active.getAttribute(attr), line: active.getAttribute('data-line') };
      }

      if (currentDialog && nextDialog) {
        currentDialog.innerHTML = nextDialog.innerHTML;
      } else {
        this.innerHTML = next.innerHTML;
      }

      if (focusKey && currentDialog) {
        let sel = focusKey.val ? `[${focusKey.attr}="${focusKey.val}"]` : `[${focusKey.attr}]`;
        if (focusKey.line) sel += `[data-line="${focusKey.line}"]`;
        (currentDialog.querySelector(sel) || currentDialog.querySelector('[data-cart-close]'))?.focus();
      }

      const nextCount = fragment.querySelector('[data-cart-count-source]');
      if (nextCount) this.#updateCounts(Number(nextCount.dataset.cartCountSource));

      // Carry the upsell context over from the fresh render, then repopulate
      for (const attr of ['data-upsell-product', 'data-upsell-exclude', 'data-upsell-limit']) {
        const value = next.getAttribute(attr);
        if (value === null) this.removeAttribute(attr);
        else this.setAttribute(attr, value);
      }
      this.#loadUpsells();

      if (openAfter) this.open();
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Cart refresh failed:', error);
    }
  }

  async #loadUpsells() {
    const container = this.querySelector('[data-cart-upsells]');
    const list = this.querySelector('[data-cart-upsells-list]');
    const productId = this.dataset.upsellProduct;

    if (!container || !list) return;
    if (!productId) {
      container.hidden = true;
      return;
    }

    const exclude = new Set((this.dataset.upsellExclude ?? '').split(','));
    const limit = Number(this.dataset.upsellLimit || 2);

    try {
      const url = new URL(`${window.Shopify?.routes?.root ?? '/'}recommendations/products.json`, window.location.origin);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '8');
      url.searchParams.set('intent', 'related');

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const { products = [] } = await response.json();
      const picks = products.filter((p) => !exclude.has(String(p.id)) && p.available).slice(0, limit);

      list.replaceChildren();
      for (const product of picks) {
        list.append(this.#buildUpsell(product, list.dataset.textAdd));
      }
      container.hidden = picks.length === 0;
    } catch (error) {
      console.error('Cart upsells failed:', error);
      container.hidden = true;
    }
  }

  #buildUpsell(product, addLabel) {
    const item = document.createElement('div');
    item.className = 'ws-cart-drawer__upsell';

    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'ws-cart-drawer__upsell-image';
      image.src = product.featured_image;
      image.alt = '';
      image.width = 72;
      image.height = 72;
      image.loading = 'lazy';
      item.append(image);
    }

    const title = document.createElement('p');
    title.className = 'ws-cart-drawer__upsell-title';
    const link = document.createElement('a');
    link.href = product.url;
    link.textContent = product.title;
    title.append(link);

    const price = document.createElement('p');
    price.className = 'ws-cart-drawer__upsell-price';
    price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active ?? 'USD'
    }).format(product.price / 100);

    const button = document.createElement('button');
    button.className = 'ws-cart-drawer__upsell-add';
    button.type = 'button';
    button.textContent = addLabel || 'Add';
    button.addEventListener('click', async () => {
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: product.variants?.[0]?.id ?? product.id, quantity: 1 })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      } catch (error) {
        console.error('Upsell add failed:', error);
        button.removeAttribute('aria-busy');
      }
    }, { signal: this.#abort.signal });

    item.append(title, price, button);
    return item;
  }

  #updateCounts(count) {
    for (const el of document.querySelectorAll('[data-cart-count]')) {
      el.textContent = count;
      el.classList.toggle('ws-header__cart-count--empty', count === 0);
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

/**
 * Shared coupon helper for the cart drawer and the cart page.
 * Applies codes through the Cart API (`/cart/update.js` with `discount`),
 * which validates them: the response's `discount_codes` entries carry an
 * `applicable` flag. Invalid codes are rolled back and reported inline.
 */
const WsCoupon = {
  async update(discountString) {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: discountString })
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },

  appliedCodes(scope) {
    return [...scope.querySelectorAll('[data-coupon-code]')].map((el) => el.dataset.couponCode);
  },

  say(message, key, code, tone) {
    if (!message) return;
    message.textContent = (message.dataset[key] ?? '').replace('[code]', code.toUpperCase());
    message.dataset.tone = tone;
    message.hidden = false;
  },

  async apply(scope) {
    const input = scope.querySelector('[data-coupon-input]');
    const button = scope.querySelector('[data-coupon-apply]');
    const message = scope.querySelector('[data-coupon-msg]');
    const code = input?.value.trim();
    if (!code) return;

    button?.setAttribute('aria-busy', 'true');

    try {
      const existing = this.appliedCodes(scope);
      const cart = await this.update([...existing, code].join(','));
      const entry = (cart.discount_codes ?? []).find(
        (d) => d.code.toLowerCase() === code.toLowerCase()
      );

      if (entry?.applicable) {
        if (input) input.value = '';
        this.say(message, 'textApplied', code, 'success');
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
      } else {
        // Roll the rejected code back off the cart, keep valid ones.
        await this.update(existing.join(','));
        this.say(message, 'textInvalid', code, 'error');
      }
    } catch (error) {
      console.error('Coupon apply failed:', error);
      this.say(message, 'textInvalid', code, 'error');
    } finally {
      button?.removeAttribute('aria-busy');
    }
  },

  async remove(scope, code) {
    try {
      const remaining = this.appliedCodes(scope).filter(
        (c) => c.toLowerCase() !== code.toLowerCase()
      );
      await this.update(remaining.join(','));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    } catch (error) {
      console.error('Coupon remove failed:', error);
    }
  }
};

window.WsCoupon = WsCoupon;